import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Button } from "@mui/material";
import { db } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { RootState, GameData, GamelistModalDetails } from "types";

import DB from "_DatabaseFunctions/directConnect";
import CALL from "_DatabaseFunctions/cloudCalls";

import ModalComp from "app/Components/Modals/ModalTemplate";

import { createMD5hash } from "utils/createMD5hash";
import { createRequest } from "../helpers/CreateReqObj";

interface GamelistModalProps {
  dets: GamelistModalDetails;
  gameInfo: GameData;
}

function GamelistModal({ dets, gameInfo }: GamelistModalProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const { gameName, hostId } = gameInfo;
  const [open, setShow] = useState(false);
  const [modalData, setModalData] = useState<{
    title?: string;
    gameName?: string;
    msg?: string;
    type?: string;
  }>({});

  useEffect(() => {
    setShow(dets.open);
    renderModalInfo(dets.status);
  }, [dets]);

  const renderModalInfo = (status: string) => {
    if (status === "confirm") {
      setModalConfirm();
    } else {
      setModalErrorData(status);
    }
  };

  const submit = async () => {
    try {
      if (!user) return;
      const whiteblacklistDoc = await getDoc(doc(db, 'whiteblacklist', hostId));
      const data = whiteblacklistDoc.data();
      if (!data) return;
      const { whitelist, blacklist } = data;
      const uidHash = createMD5hash(user.uid);
      if (whitelist[uidHash] === true) {
        return acceptWhitelisted();
      }
      if (blacklist[uidHash] === true) {
        return rejectBlacklisted();
      }
      return sendRequest();
    } catch (err) {
      console.log("Error sending request: ", err);
    }
  };

  const acceptWhitelisted = () => {
    if (!user) return;
    const reqData = createRequest(user, gameInfo, "waitlist");
    DB.setRequest(reqData);
    DB.moveToWaitlist(reqData);
    DB.sendAcceptNotification(reqData.senderId, user.uid, reqData.gameName, reqData.gameId);
    CALL.sendAcceptedEmail(reqData.senderId, reqData.gameName);
    setModalData({
      type: "success",
      title: "Success",
      msg: `You have been added to the waitlist for ${gameInfo.gameName}`,
    });
  };

  const rejectBlacklisted = () => {
    // request is not sent instead of explicity rejected
    setShow(true);
    setModalData({
      type: "error",
      title: "Request Rejected",
      msg: `You have been blacklisted from ${gameName}`,
    });
  };

  const sendRequest = () => {
    if (!user) return;
    DB.sendJoinGameNotification(user, gameInfo);
    DB.sendJoinGameRequest(user, gameInfo);
    CALL.sendRequestReceivedEmail(gameInfo.hostId, gameName, user.displayName || 'Unknown User');
    setModalData({
      type: "success",
      title: "Success",
      msg: `Request sent to ${gameInfo.gameName} successfully`,
    });
  };

  const setModalConfirm = () => {
    setModalData({
      title: "Confirm",
      gameName,
      msg: `Join game ${gameName}?`,
      type: "info",
    });
  };

  const setModalErrorData = (status: string) => {
    const msgsMap: Record<string, string> = {
      pending: `You have already sent a request to ${gameName}`,
      waitlist: `You are currently on the waitlist for ${gameName}`,
      confirmationPending: `You are currently next for ${gameName}. Please Accept or decline now`,
      hostConfirmed: `Host has already confirmed your seat for game ${gameName}. Please go to the table now`,
      playerConfirmed: `Host has already confirmed your seat for game ${gameName}. Please go to the table now`,
    };

    setModalData({
      title: "Request Error",
      gameName,
      msg: msgsMap[status] || `Unknown status: ${status}`,
      type: "error",
    });
  };

  const modalChildren = () => (
    <div>
      {modalData.type === "info" ? (
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            submit();
          }}
        >
          Confirm
        </Button>
      ) : (
        <span />
      )}

      <Button variant="contained" color="error" onClick={() => setShow(false)}>
        Close
      </Button>
    </div>
  );

  return (
    <ModalComp
      title={modalData.title || ''}
      modalType={(modalData.type as 'success' | 'error' | 'info') || 'info'}
      msg={modalData.msg || ''}
      show={open}
      onClose={() => setShow(false)}
    >
      {modalChildren()}
    </ModalComp>
  );
}

export default GamelistModal;
